import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { FormField } from '@/components/ui/FormField'
import { PhoneField } from '@/components/ui/PhoneField'
import { ONTARIO_REGIONS } from '@/constants/ontario-regions'
import { readPendingInviteToken, clearPendingInviteToken } from '@/lib/pendingInvite'

type PickRole = 'school_admin' | 'instructor' | 'student'

const ROLE_LABELS: Record<PickRole, { short: string; desc: string }> = {
  school_admin: { short: 'Driving school', desc: 'I own or manage a school' },
  instructor:   { short: 'Instructor',     desc: 'I teach students independently or at a school' },
  student:      { short: 'Student',        desc: 'I want to track my road-test readiness' },
}

const ROLE_HOME: Record<PickRole, string> = {
  school_admin: '/app/school',
  instructor:   '/app/instructor',
  student:      '/app/student',
}

// The post-signup onboarding wizard. Register.tsx now collects only a lightweight role
// hint plus email/password — every identity/qualification field for the chosen role lives
// here instead, run once a real session exists so RLS-gated writes (school creation,
// instructor_profiles, instructor_service_areas) actually work. Everything collected here
// is also editable later in Settings. Billing configuration (private lesson rate, hourly
// expense) is deliberately NOT part of this wizard — it lives in Settings only, since it
// has nothing to do with identity or being onboarded onto the platform.
export default function SelectRole() {
  const navigate     = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, refreshProfile } = useAuth()

  const initialRole = (searchParams.get('role') as PickRole | null) ?? 'instructor'
  const [role,       setRole]       = useState<PickRole>(initialRole)
  const clearRoleAndError = (r: PickRole) => { setRole(r); setError('') }
  const [fullName,   setFullName]   = useState(user?.user_metadata?.full_name ?? '')

  // School fields — MTO licence number and address/location are deliberately NOT
  // captured here. They belong to the required post-signup business-profile wizard
  // (OnboardingWizard.tsx, migration 080) instead, which: (a) asks MTO licensing as a
  // proper Licensed/Not-licensed choice with an expiry date, not just a bare number, and
  // (b) uses a real geocoded address (AddressAutocomplete), not the hardcoded Ontario
  // city list this page used to offer here — which listed "Georgina" as a selectable
  // city with no "Keswick"/"Sutton" entry, since those are actual communities WITHIN
  // Georgina township, not interchangeable with it. Asking for either field twice (once
  // here, loosely, then again properly in the wizard) was redundant and confusing.
  const [schoolName, setSchoolName] = useState(user?.user_metadata?.school_name ?? '')

  // Instructor fields
  const [phone,          setPhone]          = useState<string | null>(null)
  const [licenceNum,     setLicenceNum]     = useState('')
  const [serviceCities,  setServiceCities]  = useState<Set<string>>(new Set())

  // Student fields
  const [dob,       setDob]       = useState('')
  const [g1Licence, setG1Licence] = useState('')
  const [g1Issue,   setG1Issue]   = useState('')

  const [inviteToken, setInviteToken] = useState(readPendingInviteToken() ?? '')
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState('')
  const [prefillNotice, setPrefillNotice] = useState('')

  // Looks up whatever the school already entered at intake (full name, DOB, G1 licence/
  // issue date) for this invite code, so the student reviews/corrects it instead of
  // blindly re-typing it from scratch -- debounced on the token field so it fires once
  // typing settles rather than on every keystroke. Only fills a field that is still
  // EMPTY (never overwrites something the student already typed), and the actual accept
  // still goes through accept_invitation() entirely independently, which now gives the
  // student's own submitted value priority over this same intake data (migration 096) --
  // this lookup is purely a convenience starting point, never a trusted source of truth.
  useEffect(() => {
    if (role !== 'student' || inviteToken.trim().length < 6) { setPrefillNotice(''); return }
    const token = inviteToken.trim()
    const timer = setTimeout(async () => {
      const { data } = await supabase.rpc('preview_invitation_details', { p_raw_token: token })
      const result = data as { ok: boolean; fullName?: string | null; dateOfBirth?: string | null; g1LicenceNumber?: string | null; g1IssueDate?: string | null } | null
      if (!result?.ok) { setPrefillNotice(''); return }
      let filledAny = false
      if (result.fullName && !fullName.trim()) { setFullName(result.fullName); filledAny = true }
      if (result.dateOfBirth && !dob) { setDob(result.dateOfBirth); filledAny = true }
      if (result.g1LicenceNumber && !g1Licence.trim()) { setG1Licence(result.g1LicenceNumber); filledAny = true }
      if (result.g1IssueDate && !g1Issue) { setG1Issue(result.g1IssueDate); filledAny = true }
      setPrefillNotice(filledAny ? 'Filled in from what your school already has on file — review and correct anything below.' : '')
    }, 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken, role])

  const toggleCity = (city: string) => {
    setServiceCities(prev => {
      const next = new Set(prev)
      if (next.has(city)) next.delete(city)
      else next.add(city)
      return next
    })
  }

  const validate = (): string | null => {
    if (!fullName.trim())                              return 'Your full name is required.'
    if (role === 'school_admin' && !schoolName.trim())  return 'School name is required.'
    if (role === 'student' && !inviteToken.trim())      return 'Invite code is required.'
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (loading || !user) return
    const validationError = validate()
    if (validationError) { setError(validationError); return }
    setError('')
    setLoading(true)
    try {
      // Always fetch the live user from the server to avoid stale cached session IDs
      // (critical after account deletion + re-signup with the same OAuth provider)
      const { data: { user: freshUser }, error: userFetchError } = await supabase.auth.getUser()
      if (userFetchError || !freshUser) {
        setError('Session expired. Please sign in again.')
        return
      }
      const userId    = freshUser.id
      const userEmail = freshUser.email ?? ''

      // For students: call the server-side accept-invite Edge Function BEFORE
      // writing primary_role to the profiles table. This prevents a ghost-student
      // state where the profile says role=student but no student_profiles or
      // instructor_student_relationships row exists.
      if (role === 'student') {
        const { data: acceptResult, error: acceptError } = await supabase.functions.invoke(
          'accept-invite',
          { body: { rawToken: inviteToken.trim(), dateOfBirth: dob || null, g1LicenceNumber: g1Licence || null, g1IssueDate: g1Issue || null } }
        )

        if (acceptError) {
          setError('Could not reach the server. Please check your connection and try again.')
          return
        }

        clearPendingInviteToken()
        if (!acceptResult?.ok) {
          const code = acceptResult?.code ?? ''
          const msg =
            code === 'email_mismatch'    ? 'This invite code was not issued to your email address.' :
            code === 'expired'           ? 'This invite code has expired. Ask your instructor for a new one.' :
            code === 'already_used'      ? 'This invite code has already been used.' :
            code === 'not_pending'       ? 'This invite code is no longer valid.' :
            code === 'already_connected' ? 'You are already connected to this instructor.' :
            'Invite code is invalid. Please check the code and try again.'
          setError(msg)
          return
        }
      }

      // Upsert profile only after the invite has been accepted (or for non-student roles).
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id:           userId,
          email:        userEmail,
          full_name:    fullName.trim(),
          primary_role: role,
          phone:        role === 'instructor' ? phone : undefined,
        }, { onConflict: 'id' })
      if (profileError) {
        console.error('Profile upsert error:', profileError)
        setError(`Could not save your profile: ${profileError.message}`)
        return
      }

      if (role === 'school_admin') {
        // create_school() is a SECURITY DEFINER RPC that atomically creates the school
        // and the OWNER membership in one transaction. The user_id and role are derived
        // server-side from auth.uid() — the client only supplies the school name.
        const { error: schoolErr } = await supabase.rpc('create_school', { p_name: schoolName.trim() })
        if (schoolErr) {
          console.error('School creation error:', schoolErr)
          setError(`Could not create school: ${schoolErr.message}`)
          return
        }
        // MTO licence number and location are captured in the required post-signup
        // wizard (OnboardingWizard.tsx) instead — see the school-fields comment above.
      }

      if (role === 'instructor') {
        // ignoreDuplicates: true -> INSERT ... ON CONFLICT (user_id) DO NOTHING
        // Never overwrites an existing instructor profile (e.g. one created by Login.tsx recovery).
        await supabase.from('instructor_profiles').upsert(
          { user_id: userId, licence_number: licenceNum.trim() || null },
          { onConflict: 'user_id', ignoreDuplicates: true }
        )
        if (serviceCities.size > 0) {
          const rows = Array.from(serviceCities).map(city => ({
            instructor_id: userId,
            region: ONTARIO_REGIONS.find(r => r.cities.includes(city))?.region ?? 'Other',
            city,
          }))
          await supabase.from('instructor_service_areas').upsert(rows, { onConflict: 'instructor_id,city' })
        }
      }

      await refreshProfile()
      // A brand-new school is always gated by the required business-profile wizard
      // (migration 080's requires_profile_wizard default) — send the admin there instead
      // of straight to the dashboard. Everything collected there is also editable later
      // in Settings.
      navigate(role === 'school_admin' ? '/app/school/onboarding-wizard' : ROLE_HOME[role], { replace: true })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} />
        </div>

        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px',
        }}>
          <div style={{ font: '600 22px Lora,serif', color: '#1B2A4A', textAlign: 'center' }}>
            Welcome to DriveTrack
          </div>
          <div style={{ font: '400 13px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', textAlign: 'center', marginTop: 5, marginBottom: 20 }}>
            One more step — tell us about yourself. You can edit all of this later in Settings.
          </div>

          {/* Role picker */}
          <div className='g3' style={{ marginBottom: 20 }}>
            {(Object.entries(ROLE_LABELS) as [PickRole, { short: string; desc: string }][]).map(([r, info]) => (
              <div
                key={r}
                onClick={() => clearRoleAndError(r)}
                style={{
                  cursor: 'pointer', background: '#fff',
                  border: `2px solid ${role === r ? '#0F7C6C' : 'rgba(27,42,74,0.1)'}`,
                  borderRadius: 12, padding: 14,
                }}
              >
                <div style={{ font: '600 13px Inter,sans-serif', color: '#1B2A4A' }}>{info.short}</div>
                <div style={{ font: '400 11.5px/1.4 Inter,sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 3 }}>{info.desc}</div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FormField
              label='Full name'
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder='Your full name'
              required
            />

            {role === 'school_admin' && (
              <FormField
                label='School name'
                value={schoolName}
                onChange={e => setSchoolName(e.target.value)}
                placeholder='Maple Grove Driving School'
                required
              />
            )}

            {role === 'instructor' && (
              <>
                <div className='fg2'>
                  <PhoneField value={phone} onChange={setPhone} />
                  <FormField label='Instructor licence #' value={licenceNum} onChange={e => setLicenceNum(e.target.value)} placeholder='INS-000000' />
                </div>
                <div>
                  <label className='field-label'>Cities you teach in (select any number, across any regions)</label>
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid rgba(27,42,74,0.12)', borderRadius: 10, padding: 10 }}>
                    {ONTARIO_REGIONS.map(({ region, cities }) => (
                      <div key={region} style={{ marginBottom: 8 }}>
                        <div style={{ font: '600 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 4 }}>{region}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {cities.map(city => {
                            const checked = serviceCities.has(city)
                            return (
                              <div
                                key={city}
                                onClick={() => toggleCity(city)}
                                style={{
                                  cursor: 'pointer', padding: '4px 10px', borderRadius: 8,
                                  border: `1px solid ${checked ? '#0F7C6C' : 'rgba(27,42,74,0.15)'}`,
                                  background: checked ? 'rgba(15,124,108,0.1)' : '#fff',
                                  font: '400 12px Inter,sans-serif', color: checked ? '#0F7C6C' : '#1B2A4A',
                                }}
                              >
                                {city}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {role === 'student' && (
              <>
                <FormField
                  label='Invite code'
                  value={inviteToken}
                  onChange={e => setInviteToken(e.target.value)}
                  placeholder='From your instructor'
                  required
                />
                {prefillNotice && (
                  <div style={{ font: '400 12.5px Inter, sans-serif', color: '#0F7C6C', marginTop: -8 }}>
                    {prefillNotice}
                  </div>
                )}
                <div className='fg2'>
                  <FormField label='Date of birth' value={dob} onChange={e => setDob(e.target.value)} type='date' />
                  <FormField label='G1 licence #'  value={g1Licence} onChange={e => setG1Licence(e.target.value)} placeholder='R1234-56789' />
                </div>
                <div className='fg2'>
                  <FormField label='G1 issue date' value={g1Issue} onChange={e => setG1Issue(e.target.value)} type='date' />
                </div>
              </>
            )}

            {error && <div className='banner-error'>{error}</div>}
            <button type='submit' className='btn-primary' disabled={loading || !user}>
              {loading ? 'Saving…' : 'Complete setup'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
